/**
 * Potter's Vineyard "Proceed to Checkout" parity — old prod main.js checkoutBtn
 * handler: showCheckoutOptions("cart") → showCartToast() → openAgent().
 *
 * checkout_c7.js (reviewed flow) posts the Shipping/Pickup options on
 * ws-checkout; this listener runs after it (registered later in index.js),
 * shows the "Updating your cart..." toast over a dimmed panel, then reloads the
 * page with ?openagent=true so the winery site's Commerce7 cart syncs and the
 * agent reopens on the restored conversation (chat-history persists the options
 * message within 300ms, well before the reload). Minimizing/closing the panel
 * or clearing the chat cancels the reload.
 *
 * After that reload the chat must land on the Shipping/Pickup options, not the
 * top. MessageList's rehydrate pin runs while the panel is still hidden (its
 * scrollHeight is 0), so a one-shot sessionStorage flag set before reloading
 * asks for ws-scroll-bottom on the first open of the next page load.
 */
import { on, emit, getMessages } from "../../core/state.js";

// Old openAgent() defaulted to 3000ms; Potters asked for a 1000ms reload.
const RELOAD_DELAY_MS = 1000;
const REOPEN_KEY = "ws_pv_checkout_reopen";

const CART_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2 3h3l3 13h11l3-9H6M9 20h.01M18 20h.01"/></svg>';

function chatPanel() {
  return document.querySelector("#redconcierge-widget")?.shadowRoot?.querySelector("#ws-chat-panel") || null;
}

function markReopen() {
  try {
    sessionStorage.setItem(REOPEN_KEY, "1");
  } catch {
    /* storage blocked — the chat still reopens, just not scrolled */
  }
}

// Read-and-clear, so only the page load right after the checkout reload scrolls.
function consumeReopen() {
  try {
    const pending = sessionStorage.getItem(REOPEN_KEY) === "1";
    sessionStorage.removeItem(REOPEN_KEY);
    return pending;
  } catch {
    return false;
  }
}

export function initCheckoutRefresh() {
  let timer;
  let overlay;

  // Returning from the checkout reload: pin the restored transcript to the
  // bottom once the panel is actually visible. This listener is registered
  // before ChatPanel's, so defer a frame to let it apply [open] first.
  let scrollOnOpen = consumeReopen();
  if (scrollOnOpen) {
    on("ws-toggle", ({ isOpen } = {}) => {
      if (!isOpen || !scrollOnOpen) return;
      scrollOnOpen = false;
      requestAnimationFrame(() => emit("ws-scroll-bottom", { settle: 30 }));
    });
  }

  const cancel = () => {
    clearTimeout(timer);
    overlay?.remove();
    overlay = null;
  };

  on("ws-checkout", () => {
    cancel();
    // Only when checkout actually started (an empty cart posts the empty-cart
    // message instead of the options).
    const last = getMessages().at(-1);
    if (last?.kind !== "checkout-options") return;
    const panel = chatPanel();
    if (!panel) return;

    overlay = document.createElement("div");
    overlay.className = "ws-pv-cart-overlay";
    const toast = document.createElement("div");
    toast.className = "ws-pv-cart-toast";
    toast.setAttribute("role", "status");
    const icon = document.createElement("span");
    icon.className = "ws-pv-cart-toast-icon";
    icon.innerHTML = CART_ICON;
    const body = document.createElement("div");
    const title = document.createElement("strong");
    title.textContent = "Updating your cart...";
    const text = document.createElement("p");
    text.textContent = "Please wait while we update your cart";
    body.append(title, text);
    toast.append(icon, body);
    overlay.append(toast);
    panel.appendChild(overlay);

    timer = setTimeout(() => {
      markReopen();
      const url = new URL(window.location.href);
      url.searchParams.set("openagent", "true");
      window.location.assign(url.href);
    }, RELOAD_DELAY_MS);
  });

  on("ws-toggle", ({ isOpen } = {}) => {
    if (!isOpen) cancel();
  });
  on("ws-messages-cleared", cancel);
}
